// 📞 iOS 네이티브 통화 미디어 브릿지 (2026-09-17 맥 우편함 (3) 계약 + 윈도우 (2) 추가분)
//   WKWebView 안의 WebRTC 는 오디오 출력 경로를 앱이 바꿀 수 없어(항상 스피커 — 빌드 13~21·Safari 교차검증으로 확정)
//   iOS 셸이 RTCPeerConnection·getUserMedia 자리만 네이티브(stasel/WebRTC)로 대신한다. **웹이 통화의 주인**이다 —
//   UI·상태기·busy/타임아웃·[TPT_CALL] 마커·시그널링 REST(call/start·signal·state·end)는 CallDock 이 그대로 하고,
//   이 파일은 SDP 문자열과 상태 이벤트만 오가게 하는 어댑터다. 안드로이드·데스크톱 경로는 한 줄도 바뀌지 않는다.
//
//   셸(iOS 만 주입 — 안드 셸엔 없으므로 **메서드 존재로 기능 탐지**, isIosApp() 이 아니다):
//     nativeCallCreateOffer(callId)              마이크 잡고 offer 생성 → 이벤트 type:'offer'
//     nativeCallAcceptOffer(callId, remoteSdp)   상대 offer 로 answer 생성 → 이벤트 type:'answer'
//     nativeCallSetAnswer(callId, remoteSdp)     발신측이 받은 answer 적용
//     nativeCallEnd(callId)                      종료·자원 해제 (🔴 어떤 경로로 끝나든 반드시 호출 — 마이크·세션이 남는다)
//     nativeCallSetSpeaker(on) / nativeCallSetMuted(on)
//     nativeCallDcSend(callId, json)             (추가 ①) DataChannel 'tpt-ctl' 송신 — 없으면 상대 종료(bye)·음소거 표시가 오지 않는다
//   이벤트 window 'tpt:nativecall' detail = { callId, type, sdp?, state?, reason?, data?, aIn?, aOut? }
//     type 'offer'|'answer' → sdp   /  'state' → state 'connecting'|'connected'|'disconnected'|'ended'|'failed' (+reason)
//     'dc' → data(json 문자열)      /  'stats' → aIn·aOut (bytes 누계, 4초 주기)
//   🔴 브릿지 호출은 비동기 — 즉시 반환하고 결과는 이벤트로 온다(WKWebView 메시지 핸들러가 단방향). callId 로 짝을 맞춘다.
//   🔴 호출은 반드시 `.call(bridge, …)` — 언바인드 호출 함정(GUIDE). ICE 서버(/api/tpt/call/config)는 네이티브가 직접 가져온다.

export type NativeCallState = 'connecting' | 'connected' | 'disconnected' | 'ended' | 'failed';

type NativeCallBridge = {
	nativeCallCreateOffer?: (callId: string) => void;
	nativeCallAcceptOffer?: (callId: string, remoteSdp: string) => void;
	nativeCallSetAnswer?: (callId: string, remoteSdp: string) => void;
	nativeCallEnd?: (callId: string) => void;
	nativeCallSetSpeaker?: (on: boolean) => void;
	nativeCallSetMuted?: (on: boolean) => void;
	nativeCallDcSend?: (callId: string, json: string) => void;
};
type NativeCallEvent = {
	callId?: string;
	type?: 'offer' | 'answer' | 'state' | 'dc' | 'stats';
	sdp?: string;
	state?: NativeCallState;
	reason?: string;
	data?: string;
	aIn?: number;
	aOut?: number;
};
type Handlers = {
	onState?: (state: NativeCallState, reason?: string) => void;
	onDc?: (json: string) => void;
};

const EVENT = 'tpt:nativecall';
const STAGE_TIMEOUT_MS = 15_000;   // offer/answer 이벤트 상한 — 마이크 권한 대기 등으로 더 걸릴 수 있으면 맥과 조정

function bridge(): NativeCallBridge | null {
	if (typeof window === 'undefined') return null;
	const b = (window as unknown as { TPTAndroid?: NativeCallBridge }).TPTAndroid;
	return b && typeof b.nativeCallCreateOffer === 'function' && typeof b.nativeCallAcceptOffer === 'function'
		&& typeof b.nativeCallSetAnswer === 'function' && typeof b.nativeCallEnd === 'function' ? b : null;
}

/** 셸이 네이티브 통화 미디어를 제공하는가 — CallDock 이 음성(voice) 모드에서 이 값으로 경로를 가른다. */
export function nativeCallAvailable(): boolean {
	return !!bridge();
}

/** 실패 사유(맥 계약 ③) 중 마이크 계열 — 권한 안내 문구로 갈 것. 'mic-denied' | 'mic-busy'. */
export function isMicFailReason(reason: string | undefined): boolean {
	return reason === 'mic-denied' || reason === 'mic-busy';
}

function failErr(reason: string): Error {
	const err = new Error(reason);
	err.name = 'NativeCallFailed';
	return err;
}

type Pending = { resolve: (sdp: string) => void; reject: (e: Error) => void; timer: number };

/** 통화 1건(callId)의 네이티브 미디어 세션 — 이벤트를 Promise 로 바꾸고, 상태·dc 를 핸들러로 넘긴다. */
export class NativeCallSession {
	readonly callId: string;
	/** 마지막 stats(bytes 누계) — CallDock 진단 루프가 4초마다 읽어 증가 여부를 본다. 이벤트가 안 오면 null(진단 생략). */
	lastStats: { aIn: number; aOut: number } | null = null;
	private handlers: Handlers;
	private pendingOffer: Pending | null = null;
	private pendingAnswer: Pending | null = null;
	private ended = false;
	private readonly listener: (e: Event) => void;

	constructor(callId: string, handlers: Handlers) {
		this.callId = callId;
		this.handlers = handlers;
		this.listener = (e: Event) => this.onEvent((e as CustomEvent<NativeCallEvent>).detail || {});
		window.addEventListener(EVENT, this.listener);
	}

	private onEvent(d: NativeCallEvent) {
		if (this.ended) return;
		// callId 가 실린 이벤트만 내 것으로 — 통화가 빠르게 재시작되면 이전 세션 이벤트가 섞여 올 수 있다(맥 계약).
		//   callId 가 비어 오는 이벤트는 관용적으로 받는다.
		if (d.callId && d.callId !== this.callId) return;
		switch (d.type) {
			case 'offer': this.settle('offer', d.sdp); return;
			case 'answer': this.settle('answer', d.sdp); return;
			case 'dc': if (typeof d.data === 'string') this.handlers.onDc?.(d.data); return;
			case 'stats':
				if (typeof d.aIn === 'number' || typeof d.aOut === 'number') this.lastStats = { aIn: Number(d.aIn) || 0, aOut: Number(d.aOut) || 0 };
				return;
			case 'state': {
				const st = d.state;
				if (!st) return;
				if (st === 'failed') {
					// offer/answer 대기 중 실패 → 그 Promise 를 사유로 거절 (호출부가 안내·정리)
					const err = failErr(d.reason || 'failed');
					this.pendingOffer?.reject(err); this.pendingAnswer?.reject(err);
					this.clearPending();
				}
				this.handlers.onState?.(st, d.reason);
				return;
			}
			default: return;
		}
	}

	private settle(kind: 'offer' | 'answer', sdp: string | undefined) {
		const p = kind === 'offer' ? this.pendingOffer : this.pendingAnswer;
		if (!p) return;
		window.clearTimeout(p.timer);
		if (kind === 'offer') this.pendingOffer = null; else this.pendingAnswer = null;
		if (typeof sdp === 'string' && sdp) p.resolve(sdp);
		else p.reject(failErr('sdp'));
	}

	private clearPending() {
		if (this.pendingOffer) { window.clearTimeout(this.pendingOffer.timer); this.pendingOffer = null; }
		if (this.pendingAnswer) { window.clearTimeout(this.pendingAnswer.timer); this.pendingAnswer = null; }
	}

	private wait(kind: 'offer' | 'answer'): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const timer = window.setTimeout(() => {
				if (kind === 'offer') this.pendingOffer = null; else this.pendingAnswer = null;
				reject(failErr('timeout'));
			}, STAGE_TIMEOUT_MS);
			const p: Pending = { resolve, reject, timer };
			if (kind === 'offer') this.pendingOffer = p; else this.pendingAnswer = p;
		});
	}

	/** 발신 — 마이크 획득 + offer 생성. 반환 SDP 는 ICE 수집 완료본(웹은 non-trickle). */
	createOffer(): Promise<string> {
		const b = bridge();
		if (!b || this.ended) return Promise.reject(failErr('bridge'));
		const p = this.wait('offer');
		try { b.nativeCallCreateOffer!.call(b, this.callId); } catch (e) { this.clearPending(); return Promise.reject(e as Error); }
		return p;
	}

	/** 수신 — 상대 offer 로 answer 생성(마이크 획득 포함). 상대 offer 의 video m-line 은 셸이 inactive 로 응답한다(추가 ⑤). */
	acceptOffer(remoteSdp: string): Promise<string> {
		const b = bridge();
		if (!b || this.ended) return Promise.reject(failErr('bridge'));
		const p = this.wait('answer');
		try { b.nativeCallAcceptOffer!.call(b, this.callId, remoteSdp); } catch (e) { this.clearPending(); return Promise.reject(e as Error); }
		return p;
	}

	/** 발신측 — 상대 answer 적용. 결과는 state 이벤트(connecting→connected)로 온다. */
	setAnswer(remoteSdp: string): void {
		const b = bridge();
		if (!b || this.ended) return;
		try { b.nativeCallSetAnswer!.call(b, this.callId, remoteSdp); } catch { /* 셸이 state failed 로 알린다 */ }
	}

	setSpeaker(on: boolean): void {
		const b = bridge();
		try { b?.nativeCallSetSpeaker?.call(b, on); } catch { /* */ }
	}

	setMuted(on: boolean): void {
		const b = bridge();
		try { b?.nativeCallSetMuted?.call(b, on); } catch { /* */ }
	}

	/** DataChannel 'tpt-ctl' 송신 — 셸이 지원 안 하면 false (호출부는 조용히 생략). */
	dcSend(obj: Record<string, unknown>): boolean {
		const b = bridge();
		if (!b || typeof b.nativeCallDcSend !== 'function' || this.ended) return false;
		try { b.nativeCallDcSend.call(b, this.callId, JSON.stringify(obj)); return true; } catch { return false; }
	}

	/** 종료 — 멱등. 리스너 해제 + 대기 Promise 거절 + 셸 자원 해제. cleanup 의 모든 경로에서 불려야 한다. */
	end(): void {
		if (this.ended) return;
		this.ended = true;
		window.removeEventListener(EVENT, this.listener);
		const err = failErr('ended');
		this.pendingOffer?.reject(err); this.pendingAnswer?.reject(err);
		this.clearPending();
		const b = bridge();
		try { b?.nativeCallEnd?.call(b, this.callId); } catch { /* */ }
	}
}
